"use client";

// ── Общая оркестрация запросов ──────────────────────────────────────────────
// Стандарт для НОВОГО кода вместо ручного useState(loading/error) + useEffect
// + cancelled-флага в каждом компоненте (таких по кодовой базе ~88 — мигрируем
// по мере касания).
//
// useApiQuery — загрузка данных с защитой от гонок: у каждого запуска свой
// runId, поздний ответ устаревшего запуска (сменили кабинет/вкладку)
// отбрасывается и не перезаписывает свежие данные.
// useStatusPoll — опрос статуса фоновой операции (синк и т.п.) до завершения.

import { useCallback, useEffect, useRef, useState } from "react";

export function useApiQuery<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[],
  opts: { enabled?: boolean } = {},
) {
  const { enabled = true } = opts;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const runIdRef = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(async () => {
    const runId = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (runId !== runIdRef.current) return; // устаревший запуск — отбрасываем
      setData(result);
      setLoading(false);
    } catch (e) {
      if (runId !== runIdRef.current) return;
      setError(e instanceof Error ? e.message : "Ошибка запроса.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void reload();
    // При смене deps/unmount инвалидируем текущий запуск — его ответ игнорируется.
    return () => { runIdRef.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reload, ...deps]);

  return { data, loading, error, reload, setData };
}

/**
 * Поллинг фоновой операции: start() запускает опрос isRunning() каждые
 * intervalMs; когда вернёт false — onDone(true); если опрос упал — onDone(false).
 * Таймер чистится на unmount и при повторном start().
 */
export function useStatusPoll(
  isRunning: () => Promise<boolean>,
  opts: { intervalMs?: number; onDone?: (ok: boolean) => void } = {},
) {
  const { intervalMs = 5000, onDone } = opts;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [active, setActive] = useState(false);
  const isRunningRef = useRef(isRunning);
  isRunningRef.current = isRunning;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setActive(false);
  }, []);

  const start = useCallback(() => {
    stop();
    setActive(true);
    timerRef.current = setInterval(async () => {
      try {
        const running = await isRunningRef.current();
        if (!running) {
          stop();
          onDoneRef.current?.(true);
        }
      } catch {
        stop();
        onDoneRef.current?.(false);
      }
    }, intervalMs);
  }, [intervalMs, stop]);

  useEffect(() => stop, [stop]);

  return { active, start, stop };
}
